import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

export interface TopCallOptionI {
  id: string;
  name: string;
}

export interface CallScoreI {
  score: number | null;
  grade: string;
  sections: unknown;
  summary: string;
  scorable: boolean;
  version: string;
  model: string;
  created_at: string;
}

export interface TopCallI {
  call_id: string;
  started_at: string;
  duration_ms: number;
  rep: TopCallOptionI | null;
  workspace: TopCallOptionI | null;
  campaign: TopCallOptionI | null;
  account_name: string;
  contact_name: string;
  contact_title: string;
  disposition: string | null;
  booked: boolean;
  ai_score: CallScoreI | null;
  insightfulness_score: CallScoreI | null;
  upvote_count: number;
  viewer_upvoted: boolean;
  viewer_starred: boolean;
  breakdown_count: number;
}

export interface TopCallDetailI extends TopCallI {
  account: { id: string; name: string; website: string } | null;
  contact: {
    id: string;
    name: string;
    title: string;
    email: string;
    phone: string;
    linkedin_url: string;
  } | null;
  transcript: string | null;
  audio_url: string;
  audio_expires_in_seconds: number;
}

export interface TopCallsFiltersI {
  workspaces: TopCallOptionI[];
  reps: TopCallOptionI[];
  campaigns: TopCallOptionI[];
  dispositions: string[];
}

export interface TopCallsQueryI {
  workspace_id?: string;
  rep_id?: string;
  campaign_id?: string;
  started_from?: string;
  started_before?: string;
  view: "model" | "insightful";
  disposition?: string;
  limit: number;
  cursor?: string;
}

export type CallWindowT = "7" | "30" | "90" | "lifetime";
export type RankedCallsQueryI = Omit<TopCallsQueryI, "view"> & {
  view?: TopCallsQueryI["view"];
  window: CallWindowT;
};
export type TopTenCallsQueryI = Omit<RankedCallsQueryI, "limit" | "cursor">;
export interface CallsResponseI {
  calls: TopCallI[];
  next_cursor: string | null;
}
export interface CallGateI {
  unlocked: boolean;
  pacific_date: string;
  resets_at: string;
  submitted_breakdown_count: number;
}
export interface MyCallsSummaryI {
  top_booked: TopCallI[];
  top_insightful: TopCallI[];
  greatest_hits: TopCallI[];
  window_start: string | null;
  window_end: string | null;
}
export interface BreakdownI {
  breakdown_id: string;
  author: TopCallOptionI;
  submitted_at: string;
  upvote_count: number;
  viewer_upvoted: boolean;
}
export interface PlaybackMemoI {
  memo_id: string;
  breakdown_id: string;
  author_id: string;
  offset_ms: number;
  duration_ms: number;
  audio_url: string;
  audio_expires_in_seconds: number;
}
export interface BreakdownsResponseI {
  breakdowns: BreakdownI[];
  memos: PlaybackMemoI[];
}
export interface OwnedMemoI {
  memo_id: string;
  offset_ms: number;
  duration_ms: number;
  created_at: string;
  submitted_at: string | null;
  audio_url: string | null;
  audio_expires_in_seconds: number;
}
export interface MyBreakdownResponseI {
  breakdown: Omit<BreakdownI, "submitted_at"> & {
    submitted_at: string | null;
  };
  memos: OwnedMemoI[];
}
export interface MemoUploadI {
  offset_ms: number;
  duration_ms: number;
  byte_size: number;
  content_type: string;
  sha256: string;
}
export interface MemoUploadResponseI {
  breakdown_id: string;
  expires_in_seconds: number;
  memos: Array<{
    memo_id: string;
    offset_ms: number;
    duration_ms: number;
    upload_url: string;
    required_headers: Record<string, string>;
  }>;
}

const expected404 = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404] },
});

const expectedLive = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404, 403] },
});
const callPath = (id: string) => `/v1/top_calls/${encodeURIComponent(id)}`;
const breakdownPath = (id: string, breakdownId: string) =>
  `${callPath(id)}/breakdowns/${encodeURIComponent(breakdownId)}`;

export const TopCallsAPIEndpoints = (api: AxiosInstance) => ({
  getCalls: (params: TopCallsQueryI) =>
    api.get<CallsResponseI>("/v1/top_calls", { ...expectedLive(), params }),
  getMyLive: (params: TopCallsQueryI) =>
    api.get<CallsResponseI>("/v1/top_calls/me/live", {
      ...expectedLive(),
      params,
    }),
  getRanked: (params: RankedCallsQueryI) =>
    api.get<CallsResponseI>("/v1/top_calls/ranked", {
      ...expected404(),
      params,
    }),
  getTopTen: (params: TopTenCallsQueryI) =>
    api.get<CallsResponseI>("/v1/top_calls/top10", {
      ...expected404(),
      params,
    }),
  getGate: () => api.get<CallGateI>("/v1/top_calls/gate", expected404()),
  getMySummary: () =>
    api.get<MyCallsSummaryI>("/v1/top_calls/me/summary", expected404()),
  setCallUpvote: (id: string, active: boolean) =>
    active
      ? api.put<{ call: TopCallI }>(`${callPath(id)}/upvote`)
      : api.delete<{ call: TopCallI }>(`${callPath(id)}/upvote`),
  setCallStar: (id: string, active: boolean) =>
    active
      ? api.put<{ call: TopCallI }>(`${callPath(id)}/star`)
      : api.delete<{ call: TopCallI }>(`${callPath(id)}/star`),
  getBreakdowns: (id: string, breakdownId?: string) =>
    api.get<BreakdownsResponseI>(`${callPath(id)}/breakdowns`, {
      ...expected404(),
      params: breakdownId ? { breakdown_id: breakdownId } : undefined,
    }),
  getMyBreakdown: (id: string) =>
    api.get<MyBreakdownResponseI>(
      `${callPath(id)}/breakdowns/me`,
      expected404()
    ),
  deleteMemo: (id: string, breakdownId: string, memoId: string) =>
    api.delete<{ breakdown_id: string; memo_id: string; call: TopCallI }>(
      `${breakdownPath(id, breakdownId)}/memos/${encodeURIComponent(memoId)}`
    ),
  createUploads: (id: string, memos: MemoUploadI[]) =>
    api.post<MemoUploadResponseI>(`${callPath(id)}/breakdowns/uploads`, {
      memos,
    }),
  finalizeBreakdown: (id: string, breakdownId: string) =>
    api.post<{ breakdown_id: string; submitted_at: string; call: TopCallI }>(
      `${breakdownPath(id, breakdownId)}/finalize`
    ),
  setBreakdownUpvote: (id: string, breakdownId: string, active: boolean) =>
    active
      ? api.put<{
          breakdown_id: string;
          upvote_count: number;
          viewer_upvoted: boolean;
        }>(`${breakdownPath(id, breakdownId)}/upvote`)
      : api.delete<{
          breakdown_id: string;
          upvote_count: number;
          viewer_upvoted: boolean;
        }>(`${breakdownPath(id, breakdownId)}/upvote`),
  getFilters: (workspaceId?: string) =>
    api.get<TopCallsFiltersI>("/v1/top_calls/filters", {
      ...expected404(),
      params: workspaceId ? { workspace_id: workspaceId } : undefined,
    }),
  getCall: (callId: string) =>
    api.get<{ call: TopCallDetailI }>(
      `/v1/top_calls/${encodeURIComponent(callId)}`,
      expected404()
    ),
});
